import React from 'react';
import PropTypes from 'prop-types';
import {
    Box, ButtonBase, IconButton, Stack, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ConfigCard from './ConfigCard';
import { ChipsBox, SubLabel, focusRing } from './_PdiHelpers';

// The 24 legacy field keys the storefront's detail page understands (matches
// detail.component.ts `allowedFields`). Used as the available chip palette
// for all four slots in this card. `category_N` labels are overridden by
// the shop-specific labels from `kasvars` when present.
const ALL_FIELDS = [
    { key: 'id',          i18nKey: 'cfg_pdi_details_id',          fallback: 'ID' },
    { key: 'brand',       i18nKey: 'cfg_pdi_details_brand',       fallback: 'Supplier' },
    { key: 'name',        i18nKey: 'cfg_pdi_details_name',        fallback: 'Name' },
    { key: 'reference',   i18nKey: 'cfg_pdi_details_reference',   fallback: 'Reference' },
    { key: 'color',       i18nKey: 'cfg_pdi_details_color',       fallback: 'Color' },
    { key: 'color_brand', i18nKey: 'cfg_pdi_details_color_brand', fallback: 'Variant' },
    { key: 'season',      i18nKey: 'cfg_pdi_details_season',      fallback: 'Season' },
    ...[1,2,3,4,5,6,7].map(n => ({
        key:      `category_${n}`,
        i18nKey:  `cfg_pdi_details_category_${n}`,
        fallback: `Category ${n}`,
    })),
    ...[1,2,3,4,5].map(n => ({
        key:      `description_${n}`,
        i18nKey:  `cfg_pdi_details_description_${n}`,
        fallback: `Description ${n}`,
    })),
    ...[1,2,3,4,5].map(n => ({
        key:      `detail_${n}`,
        i18nKey:  `cfg_pdi_details_detail_${n}`,
        fallback: `Details ${n}`,
    })),
];

// Four slots, each backed by one legacy settings key:
//   - browserTab     → SHOW_FIELDS_ON_DETAIL_TAB       (free-form template)
//   - pageHeading    → SHOW_FIELDS_ON_DETAIL_TITLE     (free-form template)
//   - pageSubheading → SHOW_FIELDS_ON_DETAIL_SUBHEADING (free-form template)
//   - urlSlug        → DETAIL_PAGE_URL_SECTION         (CSV — no literal text)
// The three templates support literal-text chips (for parens, separators,
// etc.); the URL slug doesn't (storefront splits on `,` and only accepts
// known field keys).

let _chipSeq = 0;
const _newId = () => `chip_${++_chipSeq}`;
export const tokenChip = (key)  => ({ id: _newId(), key });
export const textChip  = (text) => ({ id: _newId(), text });

const isTextChip  = (c) => c && c.text !== undefined;
const isTokenChip = (c) => c && c.key  !== undefined;

const SlotEditor = ({
    titleLabel, helpText, slot, used, available, allowText,
    onSet, labelFor, tr,
}) => {
    const setChip = (id, patch) => onSet(used.map(c => c.id === id ? { ...c, ...patch } : c));
    const remove  = (id)        => onSet(used.filter(c => c.id !== id));
    const append  = (chip)      => onSet([...used, chip]);

    return (
        <Box>
            <SubLabel>{titleLabel}</SubLabel>
            {helpText && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.75, lineHeight: 1.4 }}>
                    {helpText}
                </Typography>
            )}
            <ChipsBox>
                {used.map(c => isTextChip(c) ? (
                    <TextChipBubble
                        key={c.id}
                        value={c.text}
                        onChange={(v) => setChip(c.id, { text: v })}
                        onRemove={() => remove(c.id)}
                        tr={tr}
                    />
                ) : (
                    <TokenChipBubble
                        key={c.id}
                        label={labelFor(c.key)}
                        onRemove={() => remove(c.id)}
                    />
                ))}
                {used.length === 0 && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', px: 1, py: 0.5 }}>
                        {tr('cfg_pdi_title_slot_empty', 'Empty — pick a field below or add free text.')}
                    </Typography>
                )}
                {allowText && (
                    <ButtonBase
                        onClick={() => append(textChip(' '))}
                        title={tr('cfg_pdi_title_add_text', 'Add free text')}
                        sx={{
                            flexShrink: 0,
                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                            px: 1, py: 0.4,
                            borderRadius: 999,
                            border: '1px dashed', borderColor: 'divider',
                            color: 'text.secondary',
                            fontSize: '0.7rem', fontWeight: 600,
                            '&:hover': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)' },
                            ...focusRing,
                        }}
                    >
                        <TextFieldsIcon sx={{ fontSize: 13 }} />
                        {tr('cfg_pdi_title_add_text_short', '+ Text')}
                    </ButtonBase>
                )}
            </ChipsBox>
            {available.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {available.map(field => (
                        <ButtonBase
                            key={`${slot}-${field.key}`}
                            onClick={() => append(tokenChip(field.key))}
                            sx={{
                                px: 1, py: 0.4, borderRadius: 999,
                                border: '1px dashed', borderColor: 'divider',
                                color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600,
                                '&:hover': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)' },
                                ...focusRing,
                            }}
                        >
                            + {labelFor(field.key)}
                        </ButtonBase>
                    ))}
                </Box>
            )}
        </Box>
    );
};
SlotEditor.propTypes = {
    titleLabel: PropTypes.node, helpText: PropTypes.node, slot: PropTypes.string,
    used: PropTypes.array, available: PropTypes.array, allowText: PropTypes.bool,
    onSet: PropTypes.func, labelFor: PropTypes.func, tr: PropTypes.func,
};

const TokenChipBubble = ({ label, onRemove }) => (
    <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.25,
        px: 1, py: 0.25, borderRadius: 999,
        backgroundColor: 'rgba(25,118,210,0.10)',
        color: 'primary.main',
        border: '1px solid', borderColor: 'rgba(25,118,210,0.30)',
        fontSize: '0.75rem', fontWeight: 600,
    }}>
        {label}
        <IconButton size="small" onClick={onRemove} sx={{ p: 0, ml: 0.25, color: 'inherit' }}>
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
    </Box>
);
TokenChipBubble.propTypes = { label: PropTypes.node, onRemove: PropTypes.func };

const TextChipBubble = ({ value, onChange, onRemove, tr }) => (
    <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.25,
        backgroundColor: 'rgba(0,0,0,0.04)',
        border: '1px solid', borderColor: 'divider',
        borderRadius: 999,
        pl: 0.5, pr: 0.5, py: 0.25,
    }}>
        <TextField
            size="small"
            variant="standard"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={tr('cfg_pdi_title_text_placeholder', 'free text')}
            InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem', px: 0.25 } }}
            sx={{ minWidth: 24, '& input': { padding: 0, width: `${Math.max(24, (value || '').length * 7 + 8)}px` } }}
        />
        <IconButton size="small" onClick={onRemove} sx={{ p: 0, color: 'text.disabled' }}>
            <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
    </Box>
);
TextChipBubble.propTypes = { value: PropTypes.string, onChange: PropTypes.func, onRemove: PropTypes.func, tr: PropTypes.func };

const ProductTitleAndUrlCard = ({ value = {}, onChange, shopFieldLabels, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const onChangeSlot = (slotKey, used) => onChange && onChange(slotKey, { used });

    const labelFor = (k) => {
        const shopLabel = shopFieldLabels && shopFieldLabels[k];
        if (shopLabel) return shopLabel;
        const meta = ALL_FIELDS.find(f => f.key === k);
        return meta ? tr(meta.i18nKey, meta.fallback) : k;
    };

    // Only field keys that aren't already in the slot's `used` list show up
    // in the picker — adding the same field twice doesn't change what the
    // storefront renders, so deduping here keeps the UI tidy.
    const availableFor = (used) => {
        const usedKeys = new Set(used.filter(isTokenChip).map(c => c.key));
        return ALL_FIELDS.filter(f => !usedKeys.has(f.key));
    };

    const slotUsed = (slotKey) => Array.isArray(value[slotKey]?.used) ? value[slotKey].used : [];

    return (
        <ConfigCard
            title={tr('cfg_pdi_title_url_title', 'Product title & URL')}
            description={tr('cfg_pdi_title_url_desc', 'How the product is identified across the site. Add fields and free text — for example "brand (id)" yields "Braxx (2231)".')}
            theme={theme}
        >
            <Stack spacing={2.5}>
                <SlotEditor
                    titleLabel={tr('cfg_pdi_title_browser_tab', 'Browser tab title')}
                    helpText={tr('cfg_pdi_title_browser_tab_help', 'Shown in the browser tab and search results. Free text allowed (e.g. "name (reference)").')}
                    slot="browserTab"
                    used={slotUsed('browserTab')}
                    available={availableFor(slotUsed('browserTab'))}
                    allowText
                    onSet={(used) => onChangeSlot('browserTab', used)}
                    labelFor={labelFor}
                    tr={tr}
                />
                <SlotEditor
                    titleLabel={tr('cfg_pdi_title_page_heading', 'Page heading on the product page')}
                    helpText={tr('cfg_pdi_title_page_heading_help', 'The big title shown above the product. Free text allowed.')}
                    slot="pageHeading"
                    used={slotUsed('pageHeading')}
                    available={availableFor(slotUsed('pageHeading'))}
                    allowText
                    onSet={(used) => onChangeSlot('pageHeading', used)}
                    labelFor={labelFor}
                    tr={tr}
                />
                <SlotEditor
                    titleLabel={tr('cfg_pdi_title_page_subheading', 'Page subheading')}
                    helpText={tr('cfg_pdi_title_page_subheading_help', 'Smaller line shown right below the page heading.')}
                    slot="pageSubheading"
                    used={slotUsed('pageSubheading')}
                    available={availableFor(slotUsed('pageSubheading'))}
                    allowText
                    onSet={(used) => onChangeSlot('pageSubheading', used)}
                    labelFor={labelFor}
                    tr={tr}
                />
                <SlotEditor
                    titleLabel={tr('cfg_pdi_title_url_slug', 'Product URL slug')}
                    helpText={tr('cfg_pdi_title_url_slug_help', 'Becomes part of the web address (e.g. /shop/dames/broek/braxx-2231). Free text not supported here.')}
                    slot="urlSlug"
                    used={slotUsed('urlSlug')}
                    available={availableFor(slotUsed('urlSlug'))}
                    allowText={false}
                    onSet={(used) => onChangeSlot('urlSlug', used)}
                    labelFor={labelFor}
                    tr={tr}
                />
            </Stack>
        </ConfigCard>
    );
};

ProductTitleAndUrlCard.propTypes = {
    value:              PropTypes.object,
    onChange:           PropTypes.func,
    shopFieldLabels: PropTypes.object,
    theme:              PropTypes.string,
    t:                  PropTypes.func,
};

// Parse a free-form template string ("name (reference)" → chip array) by
// scanning for known field-key tokens. The longest keys are matched first so
// `category_10` (not a thing today but defensive) wouldn't be mistaken for
// `category_1`. Anything between matches becomes a literal-text chip.
export const parseTemplateToChips = (str) => {
    const s = String(str ?? '');
    if (!s) return [];
    const keys = ALL_FIELDS.map(f => f.key).slice().sort((a, b) => b.length - a.length);
    const out  = [];
    let i = 0;
    while (i < s.length) {
        // Try to match a token at position i.
        let matched = null;
        for (const k of keys) {
            if (s.substr(i, k.length) === k) {
                // Avoid mid-word matches (e.g. "brand" inside "rebrand"); we
                // only treat it as a token when the neighbouring characters
                // aren't part of a longer identifier.
                const prev = i > 0 ? s[i - 1] : '';
                const next = s[i + k.length] || '';
                const wordChar = (c) => /[A-Za-z0-9_]/.test(c);
                if (!wordChar(prev) && !wordChar(next)) {
                    matched = k;
                    break;
                }
            }
        }
        if (matched) {
            out.push(tokenChip(matched));
            i += matched.length;
        } else {
            // Accumulate literal text until the next token (or end of string).
            let textEnd = i + 1;
            while (textEnd < s.length) {
                let found = false;
                for (const k of keys) {
                    if (s.substr(textEnd, k.length) === k) {
                        const prev = s[textEnd - 1];
                        const next = s[textEnd + k.length] || '';
                        const wordChar = (c) => /[A-Za-z0-9_]/.test(c);
                        if (!wordChar(prev) && !wordChar(next)) {
                            found = true;
                            break;
                        }
                    }
                }
                if (found) break;
                textEnd++;
            }
            const text = s.slice(i, textEnd);
            if (text) out.push(textChip(text));
            i = textEnd;
        }
    }
    return out;
};

// Serialise a chip array back into the legacy template string. Tokens emit
// their key, text chips emit their literal text — concatenation preserves
// any user-typed separators (spaces, parens, dashes).
export const serializeChipsToTemplate = (chips) =>
    (chips || []).map(c => isTextChip(c) ? (c.text || '') : c.key).join('');

// For URL slug: only token chips matter, join with `,`.
export const serializeChipsToCsv = (chips) =>
    (chips || []).filter(isTokenChip).map(c => c.key).join(',');

// Parse a CSV ("category_1,category_2,reference") back into token chips
// (URL slug doesn't allow literal text).
export const parseCsvToChips = (str) =>
    String(str || '')
        .split(',')
        .map(k => k.trim())
        .filter(Boolean)
        .map(k => tokenChip(k));

export default ProductTitleAndUrlCard;
